import { ElementRefStore } from '@stores/paginated-hierarchy-items/ElementRefStore'
import HierarchyCollapseStore from '@stores/paginated-hierarchy-items/HierarchyCollapseStore'
import ItemVisibilityStore from '@stores/paginated-hierarchy-items/ItemVisibilityStore'
import { ScrollPositionStore } from '@stores/ScrollPositionStore'
import { useObserver } from 'mobx-react-lite'
import React, { forwardRef, memo, useEffect, useMemo } from 'react'
import { StickyTree } from 'react-virtualized-sticky-tree'
import ItemsContext from './contexts/items-context'
import * as css from './HeirarchyWiseItems.css'
import useHierarchyItemContext from './hooks/use-hierarchy-item-context'
import { HeirarchyWiseItemsStore as HierarchyWiseItemsStore } from './stores/HeirarchyWiseItemsStore'
import { hasWindowsOS } from '@services/commonServices'

export interface HierarchyDetails {
  [id: string]: {
    id?: string
    itemWidth?: number
    children: any[]
    startIndex: number
    endIndex: number
    depth: number
    isLeaf?: boolean
    totalItems?: number
    isOrdersListItem?: boolean
  }
}

export interface Dimension {
  width?: number
  height?: number
}

export interface IDimensionMapper {
  dimension: (id: string) => Dimension
  setDimension: (id: string, dimension: Dimension, size?) => void
}

export interface IHierarchicalItemsStore {
  elementRefStore: ElementRefStore
  hierarchyDetails: HierarchyDetails
  dimensionMapper: IDimensionMapper
  visibilityStore?: ItemVisibilityStore
  hierarchyVisibilityStore?: ItemVisibilityStore
  hierarchyCollapseStore?: HierarchyCollapseStore
  scrollPositionStore: ScrollPositionStore
  fetchData: (id: string, fromIndex?: number, toIndex?: number) => Promise<any>
}

export interface HierarchyWiseItemsProps {
  dimensions: {
    container: {
      width: number
      height: number
    }
    card?: {
      width: number
      height: number
    }
    header?: {
      height: number
    }
  }

  rootId?: string
  pageSize: number
  Renderer: any
  onScroll: any
  scrollIndex: number
  rootHeight?: number
}

export const HierarchyWiseItems = memo(
  forwardRef<StickyTree, HierarchyWiseItemsProps>(
    (
      { dimensions, rootId, pageSize, onScroll, scrollIndex, Renderer, rootHeight },
      ref
    ) => {
      return useObserver(function useHook() {
        const { store } = useHierarchyItemContext()
        const contextValue = useMemo(() => ({ store: new HierarchyWiseItemsStore() }), [])
        const {
          store: {
            getChildren,
            rowRenderer,
            setPageSize,
            setRenderer,
            setStore,
            setDimensions,
            setRootId,
            destroy,
            setRootHeight
          }
        } = contextValue
        setStore(store)
        setPageSize(pageSize)
        setDimensions(dimensions)
        setRenderer(Renderer)
        setRootId(rootId)
        setRootHeight(rootHeight)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(() => () => destroy(), [])
        const scrollRef = React.useRef<number>(null)
        const scrollToIndex = (
          scrollRef.current !== scrollIndex ? scrollIndex : null
        ) as any
        ;(scrollRef as any).current = scrollIndex
        const root = useMemo(
          () => ({
            id: rootId,
            height: rootHeight || 0,
            isSticky: true,
            stickyTop: 0,
            zIndex: 21,
            children: store?.hierarchyDetails?.[rootId]?.children
          }),
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [rootId, rootHeight, store?.hierarchyDetails?.[rootId]?.children, dimensions]
        )
        // const scrollToIndex = useScrollIndex(scrollIndex)
        return (
          <ItemsContext.Provider value={contextValue}>
            <div
              className={
                hasWindowsOS()
                  ? css['hierarchy-wise-items-container-window']
                  : css['hierarchy-wise-items-container']
              }
            >
              <StickyTree
                ref={ref}
                root={root as any}
                width={dimensions.container.width}
                height={dimensions.container.height}
                getChildren={getChildren as any}
                rowRenderer={rowRenderer}
                renderRoot={rootHeight > 0}
                overscanRowCount={pageSize || 1}
                onScroll={onScroll}
                scrollIndex={scrollToIndex}
                wrapAllLeafNodes={true}
              />
              {/* {showToViewMore && (
          <div className={css['scroll-indicator-section']}>
            <span></span>
            <div>{strings.scrollMore}</div>
          </div>
        )} */}
            </div>
          </ItemsContext.Provider>
        )
      })
    }
  )
)
